import { clsx } from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ButtonHTMLAttributes, DetailedHTMLProps } from 'react';
import { useCallback, useMemo } from 'react';

type PageBlockProps = DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> & {
    current: boolean;
};

const PageBlock = ({ current, ...props }: PageBlockProps) => (
    <button
        data-current={current || undefined}
        className={clsx(
            '-ml-px inline-flex items-center border border-neutral-200 px-4 py-2 text-sm font-medium',
            'text-muted-foreground hover:bg-neutral-100 hover:text-neutral-900',
            'data-current:border-primary data-current:bg-primary data-current:text-primary-foreground'
        )}
        type="button"
        {...props}
    />
);

const Ellipses = () => (
    <span className="text-muted-foreground -ml-px inline-flex items-center border border-neutral-200 px-4 py-1.5 text-sm font-medium">
        ...
    </span>
);

type PageButtonProps = DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> & {
    design: 'previous' | 'next';
};

const PageButton = ({ design, ...props }: PageButtonProps) => {
    return (
        <button
            className={clsx(
                'text-muted-foreground inline-flex items-center border border-neutral-200 px-3 py-1.5 text-sm font-medium',
                'hover:bg-neutral-100 hover:text-neutral-900',
                design === 'previous' && 'rounded-l',
                design === 'next' && '-ml-px rounded-r'
            )}
            type="button"
            {...props}
        >
            {design === 'previous' && <ChevronLeft aria-hidden="true" className="text-muted-foreground size-4" />}
            {design === 'next' && <ChevronRight aria-hidden="true" className="text-muted-foreground size-4" />}
        </button>
    );
};

interface PaginationProps {
    current: number;
    total: number;
    pageSize: number;
    onChange: (page: number, pageSize: number) => void;
    className?: string;
    /**
     * before break page will display all page block
     * after break page will display ellipses
     */
    breakPage?: number;
    /*
     * visible page blocks when total pages are more than break page
     */
    visiblePages?: number;
}

const Pagination = ({
    current,
    total,
    pageSize,
    onChange,
    className,
    breakPage = 6,
    visiblePages = 5,
}: PaginationProps) => {
    const totalPages = Math.ceil(total / pageSize);

    const onPrevious = useCallback(() => {
        onChange(Math.max(current - 1, 1), pageSize);
    }, [current, onChange, pageSize]);

    const onNext = useCallback(() => {
        onChange(Math.min(current + 1, totalPages), pageSize);
    }, [current, onChange, pageSize, totalPages]);

    const renderPage = useMemo(() => {
        const showPages = Math.max(visiblePages, 3);
        const pages = [];

        // if total pages less then breakPage, then show all page block
        if (totalPages <= breakPage) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(
                    <PageBlock key={`page-${i}`} current={current === i} onClick={() => onChange(i, pageSize)}>
                        {i}
                    </PageBlock>
                );
            }

            return pages;
        }

        // add first page
        pages.push(
            <PageBlock key="first-page" current={current === 1} onClick={() => onChange(1, pageSize)}>
                1
            </PageBlock>
        );

        // add page
        if (current < showPages - 1) {
            for (let i = 2; i <= showPages - 1; i++) {
                pages.push(
                    <PageBlock key={`page-${i}`} current={current === i} onClick={() => onChange(i, pageSize)}>
                        {i}
                    </PageBlock>
                );
            }
            pages.push(<Ellipses key="ellipses" />);
        } else if (current > totalPages - (showPages - 1)) {
            pages.push(<Ellipses key="ellipses" />);
            for (let i = totalPages - (showPages - 1) + 1; i <= totalPages - 1; i++) {
                pages.push(
                    <PageBlock key={`page-${i}`} current={current === i} onClick={() => onChange(i, pageSize)}>
                        {i}
                    </PageBlock>
                );
            }
        } else {
            pages.push(<Ellipses key="ellipses-a" />);
            const start = current - Math.floor((showPages - 2) / 2);
            const end = start + showPages - 2;
            for (let i = start; i < end; i++) {
                pages.push(
                    <PageBlock key={`page-${i}`} current={current === i} onClick={() => onChange(i, pageSize)}>
                        {i}
                    </PageBlock>
                );
            }
            pages.push(<Ellipses key="ellipses-b" />);
        }

        // add last page
        pages.push(
            <PageBlock key="last-page" current={current === totalPages} onClick={() => onChange(totalPages, pageSize)}>
                {totalPages}
            </PageBlock>
        );

        return pages;
    }, [breakPage, current, onChange, pageSize, totalPages, visiblePages]);

    const renderMobilePages = useMemo(() => {
        // For mobile, we show current page, and optionally first/last if they're different
        const pages = [];

        if (current > 1) {
            pages.push(
                <PageBlock key="first-page" current={false} onClick={() => onChange(1, pageSize)}>
                    1
                </PageBlock>
            );
            if (current > 2) {
                pages.push(<Ellipses key="ellipses-start" />);
            }
        }

        pages.push(
            <PageBlock key={`page-${current}`} current={true}>
                {current}
            </PageBlock>
        );

        if (current < totalPages) {
            if (current < totalPages - 1) {
                pages.push(<Ellipses key="ellipses-end" />);
            }
            pages.push(
                <PageBlock key="last-page" current={false} onClick={() => onChange(totalPages, pageSize)}>
                    {totalPages}
                </PageBlock>
            );
        }

        return pages;
    }, [current, onChange, pageSize, totalPages]);

    return (
        <nav className={`flex items-center justify-center ${className}`} data-cy="pagination">
            <div className="inline-flex">
                <PageButton design="previous" onClick={onPrevious} />
                <div className="flex md:hidden">{renderMobilePages}</div>
                <div className="hidden md:flex">{renderPage}</div>
                <PageButton design="next" onClick={onNext} />
            </div>
        </nav>
    );
};

export default Pagination;
